/**
 * TimeSection Component
 *
 * Displays time tracking analytics including stat cards, time trend chart,
 * and breakdowns by client and project.
 */

import { Box, Typography, Grid, Paper } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import StatCard from './StatCard';
import EmptyState from './EmptyState';
import { SectionSkeleton } from './AnalyticsSkeleton';
import { ANALYTICS_SPACING } from './constants';
import { TimeStats } from '../../services/analyticsApi';

interface TimeSectionProps {
  data: TimeStats | null;
  loading?: boolean;
}

// Colors for bar charts
const CLIENT_COLORS = [
  '#5D5FEF', // Elevair purple
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#14B8A6', // Teal
  '#F97316', // Orange
];

const PROJECT_COLORS = [
  '#0088FE', // Blue
  '#00C49F', // Teal
  '#FFBB28', // Yellow
  '#FF8042', // Orange
  '#8884D8', // Purple
  '#82CA9D', // Green
  '#FFC658', // Gold
  '#FF7300', // Dark orange
  '#A4DE6C', // Light green
  '#D0ED57', // Lime
];

/**
 * Format currency from cents
 */
function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

/**
 * Format date for X-axis display
 */
function formatDate(value: string): string {
  const date = new Date(value);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Format full date for tooltip
 */
function formatDateFull(value: string): string {
  const date = new Date(value);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Custom tooltip for area chart
 */
function AreaChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}) {
  if (active && payload && payload.length && payload[0]) {
    return (
      <Paper sx={{ p: 1.5, boxShadow: 2 }}>
        <Typography variant="body2" fontWeight={500}>
          {formatDateFull(label || '')}
        </Typography>
        <Typography variant="body2" color="primary">
          {payload[0].value} hour{payload[0].value !== 1 ? 's' : ''}
        </Typography>
      </Paper>
    );
  }
  return null;
}

/**
 * Custom tooltip for bar charts
 */
function BarChartTooltip({
  active,
  payload,
  nameKey,
}: {
  active?: boolean;
  payload?: Array<{ payload: Record<string, unknown> }>;
  nameKey: string;
}) {
  if (active && payload && payload.length && payload[0]) {
    const data = payload[0].payload;
    return (
      <Paper sx={{ p: 1.5, boxShadow: 2 }}>
        <Typography variant="body2" fontWeight={500}>
          {data[nameKey] as string}
        </Typography>
        <Typography variant="body2" color="primary">
          {data.hours as number} hour{(data.hours as number) !== 1 ? 's' : ''}
        </Typography>
      </Paper>
    );
  }
  return null;
}

export default function TimeSection({ data, loading = false }: TimeSectionProps) {
  // Show skeleton while loading
  if (loading) {
    return <SectionSkeleton />;
  }

  // Check for empty data state
  const isEmpty = !data || data.stats?.totalMinutes === 0;

  if (isEmpty) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={600} sx={{ mb: ANALYTICS_SPACING.titleMarginBottom }}>
          Time Tracking
        </Typography>
        <EmptyState type="time" />
      </Box>
    );
  }

  // Transform chart data for display
  const trendData = data?.charts?.timeTrend || [];
  const clientData = data?.charts?.byClient || [];
  const projectData = data?.charts?.byProject || [];

  // Build accessible descriptions for charts
  const trendChartDescription =
    trendData.length > 0
      ? `Time trend chart showing hours tracked over time.`
      : 'No time trend data available';

  const clientChartDescription =
    clientData.length > 0
      ? `Hours by client: ${clientData.map((c) => `${c.clientName}: ${c.hours}h`).join(', ')}`
      : 'No client time data available';

  const projectChartDescription =
    projectData.length > 0
      ? `Hours by project: ${projectData.map((p) => `${p.projectName}: ${p.hours}h`).join(', ')}`
      : 'No project time data available';

  return (
    <Box>
      {/* Section Title */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: ANALYTICS_SPACING.titleMarginBottom }}>
        Time Tracking
      </Typography>

      {/* Stat Cards Grid */}
      <Grid container spacing={ANALYTICS_SPACING.cardGap} sx={{ mb: ANALYTICS_SPACING.cardsToChartsGap }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Hours"
            value={data?.stats?.totalHours || 0}
            format="number"
            subtitle={`${data?.stats?.totalMinutes || 0} minutes`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Billable Hours"
            value={data?.stats?.billableHours || 0}
            format="number"
            subtitle={`${data?.stats?.billableMinutes || 0} minutes`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Billable %" value={data?.stats?.billablePercent || 0} format="percentage" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Revenue/Hour"
            value={(data?.stats?.revenuePerHour || 0) / 100}
            format="currency"
            subtitle={`${formatCurrency(data?.stats?.totalRevenue || 0)} total`}
          />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={ANALYTICS_SPACING.chartGap}>
        {/* Time Trend Area Chart */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: ANALYTICS_SPACING.sectionPadding,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" fontWeight={500} sx={{ mb: 3 }}>
              Hours Over Time
            </Typography>
            <Box component="span" sx={visuallyHidden}>
              {trendChartDescription}
            </Box>
            <Box sx={{ width: '100%', height: 250 }} aria-hidden="true">
              {trendData.length > 0 ? (
                <ResponsiveContainer>
                  <AreaChart data={trendData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: '#E5E7EB' }}
                      tickFormatter={formatDate}
                    />
                    <YAxis
                      allowDecimals={true}
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: '#E5E7EB' }}
                      tickFormatter={(value) => `${value}h`}
                    />
                    <Tooltip content={<AreaChartTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="hours"
                      name="Hours"
                      stroke="#0EA5E9"
                      fill="#0EA5E9"
                      fillOpacity={0.2}
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography color="text.secondary">No time entries for this period</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Time by Client Bar Chart */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: ANALYTICS_SPACING.sectionPadding,
              height: '100%',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" fontWeight={500} sx={{ mb: 3 }}>
              Hours by Client
            </Typography>
            <Box component="span" sx={visuallyHidden}>
              {clientChartDescription}
            </Box>
            <Box sx={{ width: '100%', height: 300 }} aria-hidden="true">
              {clientData.length > 0 ? (
                <ResponsiveContainer>
                  <BarChart data={clientData} margin={{ top: 10, right: 10, left: 10, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      dataKey="clientName"
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                      axisLine={{ stroke: '#E5E7EB' }}
                      interval={0}
                      angle={-45}
                      textAnchor="end"
                    />
                    <YAxis
                      allowDecimals={true}
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: '#E5E7EB' }}
                      tickFormatter={(value) => `${value}h`}
                    />
                    <Tooltip content={<BarChartTooltip nameKey="clientName" />} />
                    <Bar dataKey="hours" name="Hours" radius={[4, 4, 0, 0]}>
                      {clientData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={CLIENT_COLORS[index % CLIENT_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography color="text.secondary">No client time data for this period</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Time by Project Bar Chart */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: ANALYTICS_SPACING.sectionPadding,
              height: '100%',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" fontWeight={500} sx={{ mb: 3 }}>
              Hours by Project
            </Typography>
            <Box component="span" sx={visuallyHidden}>
              {projectChartDescription}
            </Box>
            <Box sx={{ width: '100%', height: 300 }} aria-hidden="true">
              {projectData.length > 0 ? (
                <ResponsiveContainer>
                  <BarChart data={projectData} margin={{ top: 10, right: 10, left: 10, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      dataKey="projectName"
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                      axisLine={{ stroke: '#E5E7EB' }}
                      interval={0}
                      angle={-45}
                      textAnchor="end"
                    />
                    <YAxis
                      allowDecimals={true}
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: '#E5E7EB' }}
                      tickFormatter={(value) => `${value}h`}
                    />
                    <Tooltip content={<BarChartTooltip nameKey="projectName" />} />
                    <Bar dataKey="hours" name="Hours" radius={[4, 4, 0, 0]}>
                      {projectData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={PROJECT_COLORS[index % PROJECT_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography color="text.secondary">No project time data for this period</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
